import { isNil } from 'lodash'
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import {
  PaymentAssignmentSortBy,
  PaginationState,
  PaymentFilters,
  PaymentType,
  TerminatedFilter,
  TestUserFilter,
} from '../Utils/types'
import { usePaymentAssignmentFilters } from './usePaymentAssignmentFilters'
import { usePaymentAssignmentQuery } from '../../../api/finance-api-client'
import { usePaymentAssignmentDetail } from './usePaymentAssignmentDetail'
import { usePaymentAssignmentCases } from './usePaymentAssignmentCases'
import { PaymentAssignmentRole } from '../Utils/roles'
import {
  parsePaymentAssignmentPaginationFromUrl,
  parsePaymentAssignmentSortFromUrl,
  syncPaymentAssignmentUrl,
} from '../Utils/paymentAssignmentUrlState'

export const PAYMENT_ASSIGNMENT_SORT_STORAGE_KEY =
  'paymentAssignmentTable:sortState'

const DEFAULT_SORT = {
  sortBy: PaymentAssignmentSortBy.PRIORITY,
  sortOrder: 'asc' as const,
}

function readSavedSort(): {
  sortBy: PaymentAssignmentSortBy
  sortOrder: 'asc' | 'desc'
} {
  try {
    const saved = localStorage.getItem(PAYMENT_ASSIGNMENT_SORT_STORAGE_KEY)
    if (!saved) return DEFAULT_SORT
    const { sortBy, sortOrder } = JSON.parse(saved)
    if (sortBy && (sortOrder === 'asc' || sortOrder === 'desc')) {
      return { sortBy: sortBy as PaymentAssignmentSortBy, sortOrder }
    }
  } catch {
    // ignore corrupted saved state
  }
  return DEFAULT_SORT
}

export const usePaymentAssignment = (options?: {
  queryEnabled?: boolean
  role?: PaymentAssignmentRole
  lockedAssigneeId?: number
  lockedEscalatorId?: number
}) => {
  const queryEnabled = options?.queryEnabled ?? true
  const {
    filters,
    updateFilter,
    getFilteredValue,
    clearAllFilters: clearAllFiltersInternal,
    handleSelectAll,
    SELECT_ALL_VALUE,
  } = usePaymentAssignmentFilters({
    role: options?.role,
    lockedAssigneeId: options?.lockedAssigneeId,
    lockedEscalatorId: options?.lockedEscalatorId,
  })

  const [pagination, setPagination] = useState<PaginationState>(() => {
    const { page, pageSize } = parsePaymentAssignmentPaginationFromUrl()
    return {
      page: page ?? 1,
      pageSize: pageSize ?? 25,
      total: 0,
    }
  })

  const [sort, setSort] = useState<{
    sortBy: PaymentAssignmentSortBy
    sortOrder: 'asc' | 'desc'
  } | null>(() => parsePaymentAssignmentSortFromUrl() ?? readSavedSort())

  const sanitizeMulti = useCallback(
    (values?: string[]) =>
      (isNil(values) ? [] : values).filter(
        (v) => !isNil(v) && v !== SELECT_ALL_VALUE
      ),
    [SELECT_ALL_VALUE]
  )

  const buildFilterParams = useCallback(
    (input: PaymentFilters) => {
      const carrierNames = Array.isArray(input.carrierNames)
        ? input.carrierNames.filter(
            (n) => typeof n === 'string' && n.trim() !== ''
          )
        : []
      const paymentTypes = sanitizeMulti(input.paymentTypes as string[]).map(
        (t) => t as PaymentType
      )
      const priorityLevels =
        Array.isArray(input.priorityLevels) && input.priorityLevels.length
          ? input.priorityLevels
          : undefined
      const statuses = sanitizeMulti(input.statuses)
      const clientPublicKeys = sanitizeMulti(input.clientPublicKeys)
      const assigneeIds =
        Array.isArray(input.assigneeIds) && input.assigneeIds.length
          ? input.assigneeIds
          : undefined
      const escalatorIds =
        Array.isArray(input.escalatorIds) && input.escalatorIds.length
          ? input.escalatorIds
          : undefined
      const paymentSubmittedByIds =
        Array.isArray(input.paymentSubmittedByIds) &&
        input.paymentSubmittedByIds.length
          ? input.paymentSubmittedByIds
          : undefined

      const now = new Date()
      const [yearStr, monthStr] = (
        isNil(input.paymentPeriod) ? '' : input.paymentPeriod
      ).split('-')
      const year = Number(yearStr) || now.getFullYear()
      const monthFromPicker = Number(monthStr)
      // Backend expects JS-style month index: 0-11
      const month =
        Number.isFinite(monthFromPicker) && monthFromPicker > 0
          ? monthFromPicker - 1
          : now.getMonth()

      const testUserFilter =
        input.testUserFilter && input.testUserFilter !== TestUserFilter.ALL
          ? input.testUserFilter
          : undefined

      const terminatedFilter =
        input.terminatedFilter &&
        input.terminatedFilter !== TerminatedFilter.ALL
          ? input.terminatedFilter
          : undefined

      const benefitEffectiveDateFrom = (
        input.benefitEffectiveDateFrom ?? ''
      ).trim()
      const benefitEffectiveDateTo = (input.benefitEffectiveDateTo ?? '').trim()
      const benefitEndDateFrom = (input.benefitEndDateFrom ?? '').trim()
      const benefitEndDateTo = (input.benefitEndDateTo ?? '').trim()

      return {
        year,
        month,
        carrierNames: carrierNames.length ? carrierNames : undefined,
        paymentTypes: paymentTypes.length ? paymentTypes : undefined,
        priorityLevels,
        statuses: statuses.length ? statuses : undefined,
        clientPublicKeys: clientPublicKeys.length
          ? clientPublicKeys
          : undefined,
        assigneeIds,
        escalatorIds,
        paymentSubmittedByIds,
        search: (isNil(input.search) ? '' : input.search).trim() || undefined,
        testUserFilter,
        terminatedFilter,
        benefitEffectiveDateFrom: benefitEffectiveDateFrom || undefined,
        benefitEffectiveDateTo: benefitEffectiveDateTo || undefined,
        benefitEndDateFrom: benefitEndDateFrom || undefined,
        benefitEndDateTo: benefitEndDateTo || undefined,
        relationships:
          Array.isArray(input.relationships) && input.relationships.length
            ? input.relationships
            : undefined,
        binderPayment: input.binderPayment,
        primaryPaymentMethods:
          Array.isArray(input.primaryPaymentMethods) &&
          input.primaryPaymentMethods.length
            ? input.primaryPaymentMethods
            : undefined,
        onlyMigrated: input.onlyMigrated ? true : undefined,
      }
    },
    [sanitizeMulti]
  )

  // Backend query: use server-side pagination + filters; carrier filter uses carrierNames (string[] from API).
  const pendingFilterParams = useMemo(
    () => buildFilterParams(filters),
    [buildFilterParams, filters]
  )

  const [appliedFilterParams, setAppliedFilterParams] =
    useState(pendingFilterParams)
  const autoApplyAfterClearAllRef = useRef(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const request = useMemo(() => {
    const limit = pagination.pageSize
    const page = pagination.page
    return {
      ...appliedFilterParams,
      limit,
      page,
      ...(sort
        ? {
            sort: sort.sortBy,
            order: (sort.sortOrder === 'asc' ? 'ASC' : 'DESC') as
              | 'ASC'
              | 'DESC',
          }
        : {}),
    }
  }, [appliedFilterParams, pagination.page, pagination.pageSize, sort])

  const appliedFilterKey = useMemo(
    () => JSON.stringify(appliedFilterParams),
    [appliedFilterParams]
  )
  const requestKey = useMemo(() => JSON.stringify(request), [request])

  const {
    data: paymentAssignmentData,
    isFetched: isPaymentAssignmentFetched,
    isError: isPaymentAssignmentError,
    isFetching: isPaymentAssignmentFetching,
  } = usePaymentAssignmentQuery(request, refreshKey, {
    retry: false,
    enabled: queryEnabled,
  })

  // Sync total count from query
  useEffect(() => {
    if (!isNil(paymentAssignmentData?.total)) {
      setPagination((prev) => ({
        ...prev,
        total: paymentAssignmentData?.total ?? prev.total,
      }))
    }
  }, [paymentAssignmentData?.total])

  const {
    cases,
    selectedRowKeys,
    handleRowSelectionChange,
    updateCaseStatus,
    bulkAssignCases,
    updateCaseEscalator,
    removeCaseById,
  } = usePaymentAssignmentCases({
    queryData: paymentAssignmentData,
    isPaymentAssignmentFetched,
    isPaymentAssignmentError,
    requestKey,
  })

  const {
    detailCaseId,
    detailCase,
    detailLoading,
    openDetailModal,
    closeDetailModal,
  } = usePaymentAssignmentDetail()

  const filteredCases = useMemo(() => [...cases], [cases])

  const migratedCount = paymentAssignmentData?.migratedCount

  // Keep the URL in sync with filters, sort, pagination and the open detail
  // modal so the current view can be copied/shared and restored on reload.
  useEffect(() => {
    syncPaymentAssignmentUrl({
      filters,
      sort,
      page: pagination.page,
      pageSize: pagination.pageSize,
      detailCaseId,
    })
  }, [filters, sort, pagination.page, pagination.pageSize, detailCaseId])

  const hasPendingFilterChanges = useMemo(() => {
    const pendingKey = JSON.stringify(pendingFilterParams)
    return pendingKey !== appliedFilterKey
  }, [pendingFilterParams, appliedFilterKey])

  const clearAllFilters = useCallback(() => {
    autoApplyAfterClearAllRef.current = true
    clearAllFiltersInternal()
  }, [clearAllFiltersInternal])

  useEffect(() => {
    if (!autoApplyAfterClearAllRef.current) return
    autoApplyAfterClearAllRef.current = false
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }))
    setAppliedFilterParams(pendingFilterParams)
    setRefreshKey((k) => k + 1)
  }, [pendingFilterParams])

  // Optionally accepts a partial filter override so callers can submit a
  // search using new filter values in the same tick that they're staged via
  // updateFilter (avoids waiting for setFilters to commit).
  const refreshPaymentAssignments = useCallback(
    (filterOverrides?: Partial<PaymentFilters>) => {
      const params = filterOverrides
        ? buildFilterParams({ ...filters, ...filterOverrides })
        : pendingFilterParams
      setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }))
      setAppliedFilterParams(params)
      setRefreshKey((k) => k + 1)
    },
    [buildFilterParams, filters, pendingFilterParams]
  )

  const refetchPaymentAssignments = useCallback(() => {
    setRefreshKey((k) => k + 1)
  }, [])

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setPagination((prev) => ({
        ...prev,
        page: prev.pageSize !== pageSize ? 1 : page,
        pageSize,
      }))
    },
    []
  )

  const handleSortChange = useCallback(
    (
      sortBy: PaymentAssignmentSortBy | null,
      sortOrder: 'asc' | 'desc' | null
    ) => {
      if (isNil(sortBy) || isNil(sortOrder)) {
        setSort(null)
        localStorage.removeItem(PAYMENT_ASSIGNMENT_SORT_STORAGE_KEY)
      } else {
        setSort({ sortBy, sortOrder })
        localStorage.setItem(
          PAYMENT_ASSIGNMENT_SORT_STORAGE_KEY,
          JSON.stringify({ sortBy, sortOrder })
        )
      }
      setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }))
    },
    []
  )

  return {
    cases,
    filteredCases,
    rawCasesCount: cases.length,
    migratedCount,
    isPaymentAssignmentFetched,
    isPaymentAssignmentError,
    isPaymentAssignmentFetching,
    selectedRowKeys,
    pagination,
    sort,
    filters,
    updateFilter,
    clearAllFilters,
    hasPendingFilterChanges,
    refreshPaymentAssignments,
    refetchPaymentAssignments,
    handlePaginationChange,
    handleSortChange,
    handleRowSelectionChange,
    getFilteredValue,
    handleSelectAll,
    SELECT_ALL_VALUE,
    updateCaseStatus,
    bulkAssignCases,
    updateCaseEscalator,
    removeCaseById,
    // Detail modal
    detailCaseId,
    detailCase,
    detailLoading,
    openDetailModal,
    closeDetailModal,
  }
}
